'use client';
import { type ReactNode, useRef } from 'react';
import { LazyMotion, domAnimation } from 'framer-motion';
import { I18nProviderClient, seedTranslations } from '@locales/client';
import { UserProvider } from '@/shared/providers/user-provider/user-provider';
import { MenuProvider } from '@/shared/providers/menu-provider';
import { NotificationsProvider } from '@/shared/providers/notifications-provider';
import { ToastProvider } from '@/shared/providers/toast-provider';
import { SwrProvider } from '@/shared/providers/swr-provider';
// import { AppProgressBar as ProgressBar } from 'next-nprogress-bar';
import { ClarityInitializer } from '@/shared/providers/clarity-initializer';
import { GtmInitializer } from '@/shared/providers/gtm-initializer';
import { DialogViewInitializer } from '@/shared/providers/dialog-view-initializer';
import { AppAuthListener } from '@/shared/providers/app-auth-listener';
import { PaymateListener } from '@/shared/providers/paymate-listener';
import { RedirectRouterArrivalBeacon } from '@/shared/providers/redirect-router-arrival-beacon';
import { IncClickTracker } from '@/shared/providers/inc-click-tracker';
import { ServiceWorkerRegistrar } from '@/shared/providers/service-worker-registrar';
import { ReloadOnOnline } from '@/shared/providers/reload-on-online';
import { brandIsPw } from '@/shared/config/brand-by-target.mjs';

interface CountersSettings {
  gtm: boolean;
  clarity: boolean;
  robots: boolean;
}

interface ProviderProps {
  locale: string;
  countersSettings: CountersSettings;
  initialTranslations?: Record<string, string>;
  initialLanguages?: unknown;
  initialPhSettings?: unknown;
  children: ReactNode;
}

function Providers({
  locale,
  countersSettings,
  initialTranslations,
  initialLanguages,
  initialPhSettings,
  children,
}: ProviderProps) {
  const seededLocaleRef = useRef<string | null>(null);
  if (initialTranslations && seededLocaleRef.current !== locale) {
    seedTranslations(locale, initialTranslations);
    seededLocaleRef.current = locale;
  }

  const swrFallback = {
    '/settings/counters': countersSettings,
    ...(initialLanguages ? { '/languages': initialLanguages } : {}),
    ...(initialPhSettings ? { 'ph-settings': initialPhSettings } : {}),
  };

  return (
    <>
      <LazyMotion features={domAnimation} strict>
        <I18nProviderClient locale={locale}>
          <ToastProvider>
            <SwrProvider fallback={swrFallback}>
              <GtmInitializer />
              <DialogViewInitializer />
              <ClarityInitializer />
              <ServiceWorkerRegistrar />
              <ReloadOnOnline />
              <UserProvider>
                <AppAuthListener />
                <PaymateListener />
                <NotificationsProvider>
                  <MenuProvider>
                    {brandIsPw && <RedirectRouterArrivalBeacon />}
                    <IncClickTracker />
                    {children}
                    {/* <ProgressBar */}
                    {/*   height="4px" */}
                    {/*   color="#131315" */}
                    {/*   options={{ showSpinner: false }} */}
                    {/*   shallowRouting */}
                    {/* /> */}
                  </MenuProvider>
                </NotificationsProvider>
              </UserProvider>
            </SwrProvider>
          </ToastProvider>
        </I18nProviderClient>
      </LazyMotion>

      <noscript>
        <a href="https://www.livechat.com/chat-with/14785209/" rel="nofollow">
          Chat with us
        </a>
        , powered by{' '}
        <a
          href="https://www.livechat.com/?welcome"
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          LiveChat
        </a>
      </noscript>

      {/* <ProgressBar */}
      {/*   height="4px" */}
      {/*   color="#131315" */}
      {/*   options={{ showSpinner: false }} */}
      {/*   shallowRouting */}
      {/* /> */}
    </>
  );
}

export { Providers };
